import React from 'react';
import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar, Cell,
  PieChart, Pie,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import {
  CHART_COLORS, CHART_CURSOR, ACTIVE_BAR, activeDot,
  ChartTooltip, ChartLegend
} from './chartTheme';

const ANIMATION_DURATION = 500;
const ANIMATION_EASING = 'ease-out';

/**
 * Card wrapper shared by all chart sections.
 * Shows an empty-state message when `hint` is provided instead of children.
 */
export const ChartCard = ({ title, icon: Icon, children, hint }) => (
  <div className="min-w-0 overflow-hidden rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
    <div className="flex min-w-0 items-center gap-2 mb-4">
      <Icon className="h-5 w-5 shrink-0 text-primary-600" />
      <h3 className="min-w-0 break-words text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
    </div>
    {hint
      ? <div className="flex items-center justify-center h-[280px] text-gray-400 dark:text-gray-500 text-sm text-center px-4">{hint}</div>
      : children}
  </div>
);

/**
 * Line chart for time-series trend data.
 * Expects data: [{ date: string, [dataKey]: number }]
 */
export const TrendLineChart = ({
  data,
  dataKey = 'count',
  color,
  name,
  height = 280,
  tickFormatter
}) => (
  <ResponsiveContainer width="100%" height={height} minWidth={0}>
    <LineChart data={data}>
      <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
      <XAxis
        dataKey="date"
        tick={{ fontSize: 11 }}
        tickFormatter={tickFormatter}
        interval="preserveStartEnd"
        className="fill-gray-500 dark:fill-gray-400"
      />
      <YAxis
        tick={{ fontSize: 12 }}
        allowDecimals={false}
        className="fill-gray-500 dark:fill-gray-400"
      />
      <Tooltip cursor={CHART_CURSOR} content={<ChartTooltip />} />
      <Line
        type="monotone"
        dataKey={dataKey}
        stroke={color}
        strokeWidth={2}
        dot={{ r: 3, fill: color }}
        activeDot={activeDot(color)}
        name={name}
        animationDuration={ANIMATION_DURATION}
        animationEasing={ANIMATION_EASING}
      />
    </LineChart>
  </ResponsiveContainer>
);

/**
 * Horizontal bar chart (layout="vertical") for ranking lists.
 * Expects data: [{ name: string, [dataKey]: number, fill?: string }]
 * If any entry has a `.fill` field, per-bar Cell colors are applied automatically.
 */
export const HorizontalBarChart = ({
  data,
  dataKey = 'total',
  color,
  name,
  height,
  yAxisWidth = 120
}) => {
  const barHeight = height ?? Math.max(300, data.length * 40);
  const hasCellColors = data.some(d => d.fill);

  return (
    <ResponsiveContainer width="100%" height={barHeight} minWidth={0}>
      <BarChart data={data} layout="vertical" margin={{ left: 10 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" horizontal={false} />
        <XAxis
          type="number"
          tick={{ fontSize: 12 }}
          allowDecimals={false}
          className="fill-gray-500 dark:fill-gray-400"
        />
        <YAxis
          dataKey="name"
          type="category"
          tick={{ fontSize: 11 }}
          width={yAxisWidth}
          className="fill-gray-500 dark:fill-gray-400"
        />
        <Tooltip cursor={CHART_CURSOR} content={<ChartTooltip />} />
        <Bar
          dataKey={dataKey}
          fill={color || CHART_COLORS[0]}
          radius={[0, 4, 4, 0]}
          name={name}
          activeBar={ACTIVE_BAR}
          animationDuration={ANIMATION_DURATION}
          animationEasing={ANIMATION_EASING}
        >
          {hasCellColors && data.map((entry, i) => (
            <Cell key={i} fill={entry.fill || CHART_COLORS[i % CHART_COLORS.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

/**
 * Donut (ring) pie chart.
 * Expects data: [{ name: string, [dataKey]: number, color?: string, fill?: string }]
 * Per-slice color comes from entry.color or entry.fill, falling back to CHART_COLORS palette.
 */
export const DonutChart = ({
  data,
  dataKey = 'value',
  height = 280
}) => (
  <ResponsiveContainer width="100%" height={height} minWidth={0}>
    <PieChart>
      <Pie
        data={data}
        dataKey={dataKey}
        nameKey="name"
        cx="50%"
        cy="50%"
        innerRadius={60}
        outerRadius={100}
        paddingAngle={2}
        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
        labelLine
        animationDuration={ANIMATION_DURATION}
        animationEasing={ANIMATION_EASING}
      >
        {data.map((entry, i) => (
          <Cell key={i} fill={entry.color || entry.fill || CHART_COLORS[i % CHART_COLORS.length]} />
        ))}
      </Pie>
      <Tooltip content={<ChartTooltip />} />
      <Legend content={<ChartLegend />} />
    </PieChart>
  </ResponsiveContainer>
);

/**
 * Grouped (side-by-side) vertical bar chart, typically for period comparisons.
 * bars: [{ key: string, name: string, color: string }]
 */
export const GroupedBarChart = ({
  data,
  bars,
  height = 350,
  xAxisKey = 'name'
}) => (
  <ResponsiveContainer width="100%" height={height} minWidth={0}>
    <BarChart data={data}>
      <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
      <XAxis
        dataKey={xAxisKey}
        tick={{ fontSize: 11 }}
        className="fill-gray-500 dark:fill-gray-400"
      />
      <YAxis
        tick={{ fontSize: 12 }}
        allowDecimals={false}
        className="fill-gray-500 dark:fill-gray-400"
      />
      <Tooltip cursor={CHART_CURSOR} content={<ChartTooltip />} />
      <Legend content={<ChartLegend />} />
      {bars.map(bar => (
        <Bar
          key={bar.key}
          dataKey={bar.key}
          name={bar.name}
          fill={bar.color}
          radius={[4, 4, 0, 0]}
          activeBar={ACTIVE_BAR}
          animationDuration={ANIMATION_DURATION}
          animationEasing={ANIMATION_EASING}
        />
      ))}
    </BarChart>
  </ResponsiveContainer>
);

/**
 * Vertical stacked bar chart (for talent type breakdowns).
 * Last bar in `bars` gets top radius [4,4,0,0]; others are flat.
 * bars: [{ key: string, name: string, color: string }]
 */
export const StackedBarChart = ({
  data,
  bars,
  height = 300
}) => {
  const angled = data.length > 5;
  return (
    <ResponsiveContainer width="100%" height={height} minWidth={0}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 11 }}
          interval={0}
          angle={angled ? -30 : 0}
          textAnchor={angled ? 'end' : 'middle'}
          height={angled ? 70 : 30}
          className="fill-gray-500 dark:fill-gray-400"
        />
        <YAxis
          tick={{ fontSize: 12 }}
          allowDecimals={false}
          className="fill-gray-500 dark:fill-gray-400"
        />
        <Tooltip cursor={CHART_CURSOR} content={<ChartTooltip />} />
        <Legend content={<ChartLegend />} />
        {bars.map((bar, i) => (
          <Bar
            key={bar.key}
            dataKey={bar.key}
            stackId="stack"
            fill={bar.color}
            name={bar.name}
            radius={i === bars.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
            activeBar={ACTIVE_BAR}
            animationDuration={ANIMATION_DURATION}
            animationEasing={ANIMATION_EASING}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

/**
 * Horizontal stacked bar chart (layout="vertical") for grouped breakdowns like Retained/Churned.
 * Last bar in `bars` gets right radius [0,4,4,0]; others are flat.
 * bars: [{ key: string, name: string, color: string }]
 */
export const StackedHorizontalBarChart = ({
  data,
  bars,
  height
}) => {
  const barHeight = height ?? Math.max(280, data.length * 44);
  return (
    <ResponsiveContainer width="100%" height={barHeight} minWidth={0}>
      <BarChart data={data} layout="vertical" margin={{ left: 10 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" horizontal={false} />
        <XAxis
          type="number"
          tick={{ fontSize: 12 }}
          allowDecimals={false}
          className="fill-gray-500 dark:fill-gray-400"
        />
        <YAxis
          dataKey="name"
          type="category"
          tick={{ fontSize: 11 }}
          width={120}
          className="fill-gray-500 dark:fill-gray-400"
        />
        <Tooltip cursor={CHART_CURSOR} content={<ChartTooltip />} />
        <Legend content={<ChartLegend />} />
        {bars.map((bar, i) => (
          <Bar
            key={bar.key}
            dataKey={bar.key}
            stackId="stack"
            fill={bar.color}
            name={bar.name}
            radius={i === bars.length - 1 ? [0, 4, 4, 0] : [0, 0, 0, 0]}
            activeBar={ACTIVE_BAR}
            animationDuration={ANIMATION_DURATION}
            animationEasing={ANIMATION_EASING}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};
